export const DRIVE_ZONE_STRAIGHT_LINE_MODE = "straight_line" as const;
export const DRIVE_ZONE_PRECOMPUTED_DRIVING_MODE = "precomputed_driving" as const;
export const DRIVE_ZONES_CUSTOMER_CONFIG_SCHEMA_VERSION =
  "streetkit-drive-zones-config-v1" as const;
export const DRIVE_ZONES_DISTANCE_UNITS = "km" as const;
export const DRIVE_ZONES_STRAIGHT_LINE_DISCLAIMER =
  "Estimated straight-line distance from selected address coordinates." as const;
export const DRIVE_ZONES_PRECOMPUTED_DRIVING_DISCLAIMER =
  "Estimated driving distance based on OpenStreetMap road data at the time the zone index was built." as const;

export const DRIVE_ZONE_MODES = [
  DRIVE_ZONE_STRAIGHT_LINE_MODE,
  DRIVE_ZONE_PRECOMPUTED_DRIVING_MODE
] as const;

export type DriveZoneMode = (typeof DRIVE_ZONE_MODES)[number];

export type DriveZoneAttribution = {
  text: string;
  urls: string[];
};

export type DriveZoneOrigin = {
  id: string;
  label: string;
  lat: number;
  lon: number;
};

export type DriveZoneRule = {
  id: string;
  label: string;
  maxDistanceKm: number;
  fee?: number | null;
  message?: string;
  displayOrder?: number;
};

export type DriveZonesCustomerConfig = {
  schemaVersion: typeof DRIVE_ZONES_CUSTOMER_CONFIG_SCHEMA_VERSION;
  customerId: string;
  version: string;
  mode: DriveZoneMode;
  origins: DriveZoneOrigin[];
  rules: DriveZoneRule[];
  disclaimer: string;
  attribution?: DriveZoneAttribution;
};

export type DriveZoneRuleMatch = {
  id: string;
  label: string;
  maxDistanceKm: number;
  fee?: number | null;
  message?: string;
  displayOrder?: number;
};

export type DriveZoneRoutingSource = {
  engine: "OSRM" | string;
  profile?: "driving" | string;
  dataset?: "OpenStreetMap" | string;
  extractDistributor?: "Geofabrik" | string;
  extractUrl?: string;
  dataTimestamp?: string | null;
  builtAt?: string | null;
};

export type DriveZonePrecomputedRecord = {
  addressId: string;
  origin: DriveZoneOrigin | null;
  mode: DriveZoneMode;
  drivingDistanceKm: number | null;
  estimatedDurationMin?: number | null;
  zone: DriveZoneRuleMatch | null;
  rule: DriveZoneRuleMatch | null;
  routingSource: DriveZoneRoutingSource | null;
  disclaimer: string;
};

export type DriveZoneRuntimeResult = {
  available: boolean;
  mode: DriveZoneMode;
  distanceKm: number | null;
  durationMin?: number | null;
  nearestOrigin: DriveZoneOrigin | null;
  zone: DriveZoneRuleMatch | null;
  rule: DriveZoneRuleMatch | null;
  message: string;
  disclaimer: string;
};

export type DriveZoneValidationIssueCode =
  | "invalid_config"
  | "invalid_schema_version"
  | "invalid_mode"
  | "invalid_text"
  | "invalid_coordinates"
  | "invalid_distance"
  | "invalid_fee"
  | "invalid_display_order"
  | "duplicate_id"
  | "missing_origins"
  | "missing_rules"
  | "unsupported_claim";

export type DriveZoneValidationIssue = {
  code: DriveZoneValidationIssueCode;
  path: string;
  message: string;
};

export type DriveZoneValidationResult = {
  valid: boolean;
  issues: DriveZoneValidationIssue[];
};

type IndexedDriveZoneRule = {
  rule: DriveZoneRule;
  index: number;
};

const UNSUPPORTED_CLAIM_PATTERNS = [
  /\bguaranteed delivery\b/i,
  /\bcourier-certified\b/i,
  /\bdelivery-certified\b/i,
  /\bnz post\b/i,
  /\bexact travel time\b/i,
  /\blive traffic\b/i,
  /\bdpid\b/i
] as const;

export function isDriveZoneMode(value: unknown): value is DriveZoneMode {
  return (
    value === DRIVE_ZONE_STRAIGHT_LINE_MODE ||
    value === DRIVE_ZONE_PRECOMPUTED_DRIVING_MODE
  );
}

export function validateDriveZoneCoordinates(
  origin: Pick<DriveZoneOrigin, "lat" | "lon">
): boolean {
  return (
    Number.isFinite(origin.lat) &&
    Number.isFinite(origin.lon) &&
    origin.lat >= -90 &&
    origin.lat <= 90 &&
    origin.lon >= -180 &&
    origin.lon <= 180
  );
}

export function sortDriveZoneRules(rules: readonly DriveZoneRule[]): DriveZoneRule[] {
  return rules
    .map((rule, index): IndexedDriveZoneRule => ({ rule, index }))
    .sort(compareDriveZoneRules)
    .map(({ rule }) => rule);
}

export function findDriveZoneRuleForDistanceKm(
  distanceKm: number,
  rules: readonly DriveZoneRule[]
): DriveZoneRule | null {
  if (!isNonNegativeDistance(distanceKm)) {
    return null;
  }

  return sortDriveZoneRules(rules).find((rule) => distanceKm <= rule.maxDistanceKm) ?? null;
}

export function toDriveZoneRuleMatch(rule: DriveZoneRule): DriveZoneRuleMatch {
  const match: DriveZoneRuleMatch = {
    id: rule.id,
    label: rule.label,
    maxDistanceKm: rule.maxDistanceKm
  };

  if ("fee" in rule) {
    match.fee = rule.fee;
  }

  if (rule.message !== undefined) {
    match.message = rule.message;
  }

  if (rule.displayOrder !== undefined) {
    match.displayOrder = rule.displayOrder;
  }

  return match;
}

export function validateDriveZonesCustomerConfig(
  config: unknown
): DriveZoneValidationResult {
  const issues: DriveZoneValidationIssue[] = [];

  if (!isRecord(config)) {
    addIssue(
      issues,
      "invalid_config",
      "$",
      "Drive Zones customer config must be an object."
    );

    return {
      valid: false,
      issues
    };
  }

  const schemaVersion = config.schemaVersion;
  const customerId = config.customerId;
  const version = config.version;
  const mode = config.mode;
  const disclaimer = config.disclaimer;

  if (schemaVersion !== DRIVE_ZONES_CUSTOMER_CONFIG_SCHEMA_VERSION) {
    addIssue(
      issues,
      "invalid_schema_version",
      "schemaVersion",
      `schemaVersion must be ${DRIVE_ZONES_CUSTOMER_CONFIG_SCHEMA_VERSION}.`
    );
  }

  if (!isNonEmptyString(customerId)) {
    addIssue(issues, "invalid_text", "customerId", "customerId must be a non-empty string.");
  } else {
    addUnsupportedClaimIssues(issues, "customerId", customerId);
  }

  if (!isNonEmptyString(version)) {
    addIssue(issues, "invalid_text", "version", "version must be a non-empty string.");
  }

  if (!isDriveZoneMode(mode)) {
    addIssue(
      issues,
      "invalid_mode",
      "mode",
      'mode must be "straight_line" or "precomputed_driving".'
    );
  }

  if (!isNonEmptyString(disclaimer)) {
    addIssue(issues, "invalid_text", "disclaimer", "disclaimer must be a non-empty string.");
  } else {
    addUnsupportedClaimIssues(issues, "disclaimer", disclaimer);
  }

  if (config.attribution !== undefined) {
    validateAttribution(config.attribution, issues);
  }

  validateOrigins(config.origins, issues);
  validateRules(config.rules, issues);

  return {
    valid: issues.length === 0,
    issues
  };
}

export function isDriveZonesCustomerConfig(config: unknown): config is DriveZonesCustomerConfig {
  return validateDriveZonesCustomerConfig(config).valid;
}

function validateAttribution(
  attribution: unknown,
  issues: DriveZoneValidationIssue[]
): void {
  if (!isRecord(attribution)) {
    addIssue(issues, "invalid_config", "attribution", "attribution must be an object.");
    return;
  }

  const text = attribution.text;
  const urls = attribution.urls;

  if (!isNonEmptyString(text)) {
    addIssue(issues, "invalid_text", "attribution.text", "attribution.text must be non-empty.");
  } else {
    addUnsupportedClaimIssues(issues, "attribution.text", text);
  }

  if (!Array.isArray(urls) || urls.length === 0) {
    addIssue(
      issues,
      "invalid_text",
      "attribution.urls",
      "attribution.urls must include at least one URL."
    );
    return;
  }

  urls.forEach((url, index) => {
    if (!isNonEmptyString(url)) {
      addIssue(
        issues,
        "invalid_text",
        `attribution.urls.${index}`,
        "attribution URL must be a non-empty string."
      );
    }
  });
}

function validateOrigins(
  origins: unknown,
  issues: DriveZoneValidationIssue[]
): void {
  if (!Array.isArray(origins) || origins.length === 0) {
    addIssue(issues, "missing_origins", "origins", "At least one Drive Zones origin is required.");
    return;
  }

  const originIds = new Set<string>();

  origins.forEach((origin, index) => {
    const path = `origins.${index}`;

    if (!isRecord(origin)) {
      addIssue(issues, "invalid_config", path, "Origin must be an object.");
      return;
    }

    const id = origin.id;
    const label = origin.label;
    const lat = origin.lat;
    const lon = origin.lon;

    if (!isNonEmptyString(id)) {
      addIssue(issues, "invalid_text", `${path}.id`, "Origin id must be a non-empty string.");
    } else if (originIds.has(id)) {
      addIssue(issues, "duplicate_id", `${path}.id`, `Origin id "${id}" is duplicated.`);
    } else {
      originIds.add(id);
    }

    if (!isNonEmptyString(label)) {
      addIssue(issues, "invalid_text", `${path}.label`, "Origin label must be non-empty.");
    } else {
      addUnsupportedClaimIssues(issues, `${path}.label`, label);
    }

    if (
      typeof lat !== "number" ||
      typeof lon !== "number" ||
      !validateDriveZoneCoordinates({ lat, lon })
    ) {
      addIssue(
        issues,
        "invalid_coordinates",
        path,
        "Origin coordinates must be finite WGS84 latitude and longitude values."
      );
    }
  });
}

function validateRules(rules: unknown, issues: DriveZoneValidationIssue[]): void {
  if (!Array.isArray(rules) || rules.length === 0) {
    addIssue(issues, "missing_rules", "rules", "At least one Drive Zones rule is required.");
    return;
  }

  const ruleIds = new Set<string>();
  const displayOrders = new Set<number>();

  rules.forEach((rule, index) => {
    const path = `rules.${index}`;

    if (!isRecord(rule)) {
      addIssue(issues, "invalid_config", path, "Rule must be an object.");
      return;
    }

    const id = rule.id;
    const label = rule.label;
    const maxDistanceKm = rule.maxDistanceKm;
    const fee = rule.fee;
    const message = rule.message;
    const displayOrder = rule.displayOrder;

    if (!isNonEmptyString(id)) {
      addIssue(issues, "invalid_text", `${path}.id`, "Rule id must be a non-empty string.");
    } else if (ruleIds.has(id)) {
      addIssue(issues, "duplicate_id", `${path}.id`, `Rule id "${id}" is duplicated.`);
    } else {
      ruleIds.add(id);
    }

    if (!isNonEmptyString(label)) {
      addIssue(issues, "invalid_text", `${path}.label`, "Rule label must be non-empty.");
    } else {
      addUnsupportedClaimIssues(issues, `${path}.label`, label);
    }

    if (typeof maxDistanceKm !== "number" || !isPositiveDistance(maxDistanceKm)) {
      addIssue(
        issues,
        "invalid_distance",
        `${path}.maxDistanceKm`,
        "Rule maxDistanceKm must be a positive finite number."
      );
    }

    if ("fee" in rule && fee !== null && !isNonNegativeFiniteNumber(fee)) {
      addIssue(
        issues,
        "invalid_fee",
        `${path}.fee`,
        "Rule fee must be null or a non-negative finite number."
      );
    }

    if (message !== undefined) {
      if (!isNonEmptyString(message)) {
        addIssue(issues, "invalid_text", `${path}.message`, "Rule message must be non-empty.");
      } else {
        addUnsupportedClaimIssues(issues, `${path}.message`, message);
      }
    }

    if (displayOrder !== undefined) {
      if (
        typeof displayOrder !== "number" ||
        !Number.isInteger(displayOrder) ||
        displayOrder < 0
      ) {
        addIssue(
          issues,
          "invalid_display_order",
          `${path}.displayOrder`,
          "Rule displayOrder must be a non-negative integer."
        );
      } else if (displayOrders.has(displayOrder)) {
        addIssue(
          issues,
          "invalid_display_order",
          `${path}.displayOrder`,
          `Rule displayOrder ${displayOrder} is duplicated.`
        );
      } else {
        displayOrders.add(displayOrder);
      }
    }
  });
}

function compareDriveZoneRules(a: IndexedDriveZoneRule, b: IndexedDriveZoneRule): number {
  const displayOrderDifference = numericRuleOrder(
    a.rule.displayOrder,
    b.rule.displayOrder,
    Number.POSITIVE_INFINITY
  );

  if (displayOrderDifference !== 0) {
    return displayOrderDifference;
  }

  const distanceDifference = a.rule.maxDistanceKm - b.rule.maxDistanceKm;

  if (distanceDifference !== 0) {
    return distanceDifference;
  }

  return a.index - b.index;
}

function numericRuleOrder(
  a: number | undefined,
  b: number | undefined,
  fallback: number
): number {
  const hasLeft = typeof a === "number" && Number.isFinite(a);
  const hasRight = typeof b === "number" && Number.isFinite(b);

  if (!hasLeft && !hasRight) {
    return 0;
  }

  const left = hasLeft ? a : fallback;
  const right = hasRight ? b : fallback;

  return left - right;
}

function isPositiveDistance(value: number): boolean {
  return Number.isFinite(value) && value > 0;
}

function isNonNegativeDistance(value: number): boolean {
  return Number.isFinite(value) && value >= 0;
}

function isNonNegativeFiniteNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value) && value >= 0;
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === "string" && value.trim().length > 0;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function addUnsupportedClaimIssues(
  issues: DriveZoneValidationIssue[],
  path: string,
  value: string
): void {
  for (const pattern of UNSUPPORTED_CLAIM_PATTERNS) {
    if (pattern.test(value)) {
      addIssue(
        issues,
        "unsupported_claim",
        path,
        "Drive Zones wording must not claim delivery certification, NZ Post status, exact travel time, live traffic, DPID support, or guaranteed delivery."
      );
      return;
    }
  }
}

function addIssue(
  issues: DriveZoneValidationIssue[],
  code: DriveZoneValidationIssueCode,
  path: string,
  message: string
): void {
  issues.push({
    code,
    path,
    message
  });
}
